import React, { useEffect, useState } from 'react';
import { getFirestore, collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { app } from '../firebase';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

const db = getFirestore(app);

// Helper to format duration
const formatDuration = (ms) => {
    if (ms < 0) ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
};

export default function Dashboard() {
    const [activeCalls, setActiveCalls] = useState([]);
    const [pastCalls, setPastCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [now, setNow] = useState(Date.now());
    const [isSystemAdmin, setIsSystemAdmin] = useState(false);
    const [userDomainId, setUserDomainId] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);

    // Check auth claims first
    useEffect(() => {
        const auth = getAuth(app);
        const unsub = onIdTokenChanged(auth, async (user) => {
            if (!user) {
                setIsSystemAdmin(false);
                setUserDomainId(null);
                setAuthChecked(true);
                return;
            }
            try {
                const tokenResult = await user.getIdTokenResult();
                setIsSystemAdmin(tokenResult?.claims?.role === 'SYSTEM_ADMIN');
                setUserDomainId(tokenResult?.claims?.domainId || null);
            } catch (e) {
                console.error(e);
            } finally {
                setAuthChecked(true);
            }
        });
        return () => unsub();
    }, []);

    // Update 'now' every second to refresh durations
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!authChecked) return;

        // For non-System Admins, we MUST have the domainId before we can construct a valid query
        if (!isSystemAdmin && !userDomainId) {
            setLoading(false);
            return;
        }

        // We are querying active calls: state in ('pending', 'ringing', 'connecting', 'connected', 'ending')
        // and ordering by createdAt. This will require a composite index in Firestore.
        const activeStates = ['pending', 'ringing', 'connecting', 'connected', 'ending'];

        // Create query
        let q = query(
            collection(db, 'callSessions'),
            where('state', 'in', activeStates),
            orderBy('createdAt', 'desc')
        );

        // Domain Admins MUST filter by domainId to satisfy security rules
        if (!isSystemAdmin && userDomainId) {
            q = query(
                collection(db, 'callSessions'),
                where('domainId', '==', userDomainId),
                where('state', 'in', activeStates),
                orderBy('createdAt', 'desc')
            );
        }

        const unsubActive = onSnapshot(q, (snap) => {
            const calls = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setActiveCalls(calls);
            setLoading(false);
        }, (err) => {
            console.error('Failed to fetch active calls:', err);
            setLoading(false);
        });

        // Query past calls
        const pastStates = ['ended', 'failed', 'cancelled', 'expired'];
        let qPast = query(
            collection(db, 'callSessions'),
            where('state', 'in', pastStates),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        if (!isSystemAdmin && userDomainId) {
            qPast = query(
                collection(db, 'callSessions'),
                where('domainId', '==', userDomainId),
                where('state', 'in', pastStates),
                orderBy('createdAt', 'desc'),
                limit(50)
            );
        }

        const unsubPast = onSnapshot(qPast, (snap) => {
            const calls = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPastCalls(calls);
        }, (err) => {
            console.error('Failed to fetch past calls:', err);
        });

        return () => {
            unsubActive();
            unsubPast();
        };
    }, [authChecked, isSystemAdmin, userDomainId]);

    const getStateColor = (state) => {
        switch (state) {
            case 'connected': return 'success';
            case 'connecting': return 'info';
            case 'ringing': return 'warning';
            case 'pending': return 'default';
            case 'ending': return 'error';
            default: return 'default';
        }
    };

    if (loading) return <Box sx={{ p: 2 }}>Loading dashboard...</Box>;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Active Calls</Typography>

            {activeCalls.length === 0 ? (
                <Typography>No active calls at the moment.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Initiator</TableCell>
                                <TableCell>Target (Participant / Room)</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>Duration</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {activeCalls.map((call) => {
                                const initiatorName = call.caller?.name || 'Unknown';
                                const targetName = call.targetParticipantName || call.roomName || 'Unknown';

                                // If it's a room call, display the room indicator
                                const targetDisplay = call.targetType === 'room'
                                    ? `${targetName} (Room)`
                                    : targetName;

                                // Duration Calculation
                                // Use `createdAt` if available
                                const startTimeMs = call.createdAt?.toMillis ? call.createdAt.toMillis() : null;
                                const durationText = startTimeMs ? formatDuration(now - startTimeMs) : '--';

                                return (
                                    <TableRow key={call.id} hover>
                                        <TableCell>{initiatorName}</TableCell>
                                        <TableCell>{targetDisplay}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={call.state || 'unknown'}
                                                color={getStateColor(call.state)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{durationText}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Call History (Last 50)</Typography>

            {pastCalls.length === 0 ? (
                <Typography>No call history available.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Initiator</TableCell>
                                <TableCell>Target (Participant / Room)</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Duration</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pastCalls.map((call) => {
                                const initiatorName = call.caller?.name || 'Unknown';
                                const targetName = call.targetParticipantName || call.roomName || 'Unknown';

                                const targetDisplay = call.targetType === 'room'
                                    ? `${targetName} (Room)`
                                    : targetName;

                                const startTimeMs = call.createdAt?.toMillis ? call.createdAt.toMillis() : null;
                                const endTimeMs = call.endedAt?.toMillis ? call.endedAt.toMillis() : (call.updatedAt?.toMillis ? call.updatedAt.toMillis() : null);

                                const durationText = (startTimeMs && endTimeMs && endTimeMs > startTimeMs)
                                    ? formatDuration(endTimeMs - startTimeMs)
                                    : '--';

                                const startedText = startTimeMs ? new Date(startTimeMs).toLocaleString() : '--';

                                return (
                                    <TableRow key={call.id} hover>
                                        <TableCell>{initiatorName}</TableCell>
                                        <TableCell>{targetDisplay}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={call.state || 'unknown'}
                                                color={getStateColor(call.state)}
                                                size="small"
                                            />
                                            {call.endedReason && (
                                                <Typography variant="caption" display="block" color="textSecondary">
                                                    {call.endedReason}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{startedText}</TableCell>
                                        <TableCell>{durationText}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
